import { GameLoop } from '../../../../../game';
import { BulletsEntity } from './BulletsEntity';
import { ShipEntity, ShipDirection } from './ShipEntity';
import { EnemyBulletsEntity } from './EnemyBulletsEntity';
import { SwarmEntity } from './SwarmEntity';
import {
  modulus,
  add,
  contains,
  floor,
  divide,
  subtract,
  distance,
} from '../../../../../utils';
import { PowerupEntity } from './PowerupEntity';
import { MAX_LIVES } from '.';
import { music } from '../../../../../audio';
import { ShipConfig } from '../../../../../types';

export class MainGameLoop extends GameLoop {
  ship: ShipEntity;
  bullets: BulletsEntity;
  powerup: PowerupEntity;
  swarm: SwarmEntity;
  enemyBullets: EnemyBulletsEntity;

  score = 0;

  fireHeld: boolean = false;

  constructor({ shipConfig }: { shipConfig: ShipConfig }) {
    super();

    this.ship = new ShipEntity(shipConfig);
    this.add(this.ship);

    this.bullets = new BulletsEntity();
    this.add(this.bullets);

    this.enemyBullets = new EnemyBulletsEntity();
    this.add(this.enemyBullets);

    this.swarm = new SwarmEntity();
    this.add(this.swarm);

    this.powerup = new PowerupEntity();
    this.add(this.powerup);

    window.addEventListener('keydown', this.keydown);
    window.addEventListener('keyup', this.keyup);
  }

  destroy() {
    window.removeEventListener('keydown', this.keydown);
    window.removeEventListener('keyup', this.keyup);
  }

  step(delta: number) {
    const { swarm, ship, bullets, enemyBullets, fireHeld, powerup } = this;

    bullets.step(delta);
    enemyBullets.step(delta);

    if (ship.dead) return;

    swarm.step(delta);
    ship.step(delta);
    powerup.step(delta);

    // fire own bullets
    if (fireHeld && ship.fireTime <= 0) {
      bullets.fire(add(ship.position, [0, ship.size[1], 0]));
      ship.fireTime = ship.fireRate;
      music.shipShoot();
      this.score -= 1;
    }

    // fire enemy bullets
    if (enemyBullets.timer < 0 && swarm.remaining > 0) {
      enemyBullets.fire(swarm.randomPosition());
      // increase bullet speed as swarm moves closer
      enemyBullets.timer =
        enemyBullets.minimumDuration +
        (swarm.position[1] / swarm.startingPosition[1]) *
          (enemyBullets.initialDurationUntilShot -
            enemyBullets.minimumDuration);
      music.enemyShoot();
    }

    // enemy to ship collision detection
    const shipMin = add(ship.position, [-ship.size[0] / 2, 0, 0]);
    const shipMax = add(ship.position, [
      ship.size[0] / 2,
      ship.size[1],
      ship.size[2],
    ]);
    enemyBullets.positions.forEach((position, i) => {
      if (contains(position, shipMin, shipMax)) {
        enemyBullets.positions.splice(i, 1);
        ship.hit();
        this.score -= 100;
        if (ship.dead) {
          music.gameOver();
        } else if (!ship.invincible) {
          music.shipHit();
        }
      }
    });

    if (
      powerup.visible &&
      distance(powerup.position, ship.position) <= ship.size[0]
    ) {
      powerup.visible = false;
      const handlePowerup = () => {
        powerup.roll();
        let duration = powerup.currentRoll?.duration as number;
        const { currentRoll } = powerup;
        if (!currentRoll) return;
        switch (powerup.currentRoll?.type) {
          case 'shipSpeed':
            // ship speed
            let originalVelocity = ship.maxVelocity;
            powerup.revertPowerup = () => {
              ship.maxVelocity = originalVelocity;
            };
            ship.maxVelocity =
              ship.maxVelocity + ship.maxVelocity * currentRoll.increase;
            break;
          case 'rapidFire':
            let originalFireRate = ship.fireRate;
            powerup.revertPowerup = () => {
              ship.fireRate = originalFireRate;
            };
            ship.fireRate =
              ship.fireRate - ship.fireRate * currentRoll.increase;
            break;
          case 'lifeIncrease':
            // life increase
            if (ship.lives === MAX_LIVES) {
              // re-roll
              handlePowerup();
              return;
            } else {
              ship.lives++;
              duration = 2;
            }
            break;
          case 'invincible':
            powerup.revertPowerup = () => {
              ship.invincible = false;
            };
            ship.invincible = true;
            break;
          default:
            break;
        }
        ship.powerup(duration);
        music.powerUpCollect();
      };
      handlePowerup();
    }

    if (
      powerup.currentRoll &&
      powerup.currentElapsedDuration >= powerup.currentRoll.duration
    ) {
      powerup.reset();
    }

    // ship to enemy collision detection
    const min = swarm.position;
    const max = add(swarm.size, min);
    bullets.positions.forEach((position, i) => {
      const relativePosition = subtract(position, min);
      if (
        contains(position, min, max) &&
        contains(
          modulus(relativePosition, swarm.spacing),
          [0, 0, 0],
          swarm.glyphSize,
        )
      ) {
        const v = floor(divide(relativePosition, swarm.spacing));
        if (swarm.alive[v[0]][v[1]][v[2]]) {
          swarm.hit(v);
          music.enemyHit();
          bullets.positions.splice(i, 1);
          this.score += 100;
          if (swarm.dead) {
            ship.blastOff = true;
            music.win();
          }
        }
      }
    });

    // ship dies when swarm reaches it
    const swarmMax = add(swarm.position, swarm.aliveMin());
    if (
      !swarm.dead &&
      !ship.dead &&
      swarmMax[1] < ship.position[1] + ship.size[1]
    ) {
      swarm.dead = true;
      ship.dead = true;
      this.score -= 500;
      music.shipHit();
      music.gameOver();
    }
  }

  private keydown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') {
      this.ship.direction = ShipDirection.Left;
    } else if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') {
      this.ship.direction = ShipDirection.Right;
    } else if (e.key === ' ' && !this.ship.dead && this.ship.fireTime <= 0) {
      this.fireHeld = true;
    }
  };

  private keyup = (e: KeyboardEvent) => {
    if (
      ((e.key === 'ArrowLeft' || e.key.toLowerCase() === 'a') &&
        this.ship.direction === ShipDirection.Left) ||
      ((e.key === 'ArrowRight' || e.key.toLowerCase() === 'd') &&
        this.ship.direction === ShipDirection.Right)
    ) {
      this.ship.direction = ShipDirection.Static;
    } else if (e.key === ' ') {
      this.fireHeld = false;
    }
  };
}
